// Values a person or an editor hands to the task screens, narrowed to what the task contracts accept. A field
// editor reports `unknown` (it serves every field type); these turn that into the typed value or null, so
// nothing invalid is sent on and nothing is cast.
import { z } from 'zod';
import { RecordRef } from '@dataroom/core';
import { TaskRecurrence, type TaskDTO } from '@dataroom/core/tasks';

/** The priorities a task can have, as the task contract lists them. */
export const TASK_PRIORITIES = [
  'low',
  'normal',
  'high',
  'urgent',
] as const satisfies readonly TaskDTO['priority'][];
const PrioritySchema = z.enum(TASK_PRIORITIES);

/** A priority from an editor value; null for anything that is not one. */
export function parsePriority(value: unknown): TaskDTO['priority'] | null {
  const parsed = PrioritySchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The id a person/record picker reports: a non-empty string, or null (nobody). */
export function parseId(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The record a record picker reports; null when it holds nothing (or something that is no record reference). */
export function parseRecordRef(value: unknown): RecordRef | null {
  const parsed = RecordRef.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The record a record picker reports when it holds at most one (`multiple` off). */
export function singleRef(value: RecordRef | RecordRef[] | null): RecordRef | null {
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

/** The day (`YYYY-MM-DD`) a date editor reports, or null when it is cleared or not a day. */
export function parseDay(value: unknown): string | null {
  const parsed = z.iso.date().safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** How often a task repeats, from the select's value; null for "does not repeat" (and anything unknown). */
export function parseRecurrenceFrequency(value: string): TaskRecurrence['frequency'] | null {
  const parsed = TaskRecurrence.shape.frequency.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The repeat interval from what was typed: a whole number the contract accepts (1 to 365). An empty or half-typed
 * field returns null so the editor keeps the last good interval instead of saving NaN.
 */
export function parseRecurrenceInterval(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = TaskRecurrence.shape.interval.safeParse(Number(raw));
  return parsed.success ? parsed.data : null;
}

/** The layouts of the tasks screen (`?layout=`), as the route validates them. */
export const TasksLayoutSchema = z.enum(['table', 'board', 'calendar']);
export type TasksLayout = z.infer<typeof TasksLayoutSchema>;

/** A month of the calendar layout (`?month=`): `YYYY-MM` with a real month. */
export const CalendarMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
